import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Page, PageHeader } from '@/ui/Panel';
import { SubNav, SubNavItem } from '@/ui/Filters';
import { useShortcutHint } from '@/app/StatusLine';
import { useBadges } from '@/app/useBadges';
import { StockTab } from '@/features/inventory/StockTab';
import { LowStockTab } from '@/features/inventory/LowStockTab';
import { ExpiringTab } from '@/features/inventory/ExpiringTab';
import { LotsTab } from '@/features/inventory/LotsTab';
import { TrackingTab } from '@/features/inventory/TrackingTab';
import { ImportTab } from '@/features/inventory/ImportTab';
import { CountsTab } from '@/features/inventory/CountsTab';
import {
  INVENTORY_TABS,
  TAB_LABELS,
  validateInventorySearch,
  type InventorySearch,
} from '@/features/inventory/search';

/**
 * Stock — one screen, seven views.
 *
 * Inventory is a single subject to the person doing the job, so it is a single
 * route with `?tab=` rather than seven sibling pages. Filters, the selected
 * product and the report window all live in the URL, which means the back
 * button works, a refresh is lossless, and an operator can paste "the expiring
 * list I'm looking at" into a message.
 */
export const Route = createFileRoute('/_app/inventory')({
  staticData: { crumb: 'Inventory' },
  validateSearch: validateInventorySearch,
  component: InventoryScreen,
});

const TAB_SUBTITLE: Record<InventorySearch['tab'], string> = {
  stock: 'On hand, reserved and available, with the movements behind each balance.',
  low: 'Tracked products at or below their reorder point.',
  expiring: 'Lot dates, disposal candidates and the ones missing a date entirely.',
  lots: 'Advisory lot records. Never authoritative for on-hand.',
  tracking: 'Which products carry a stock balance at all. Admin-only to change.',
  import: 'Bulk CSV stock adjustments, validated before anything is written.',
  counts: 'Count sheets applied as of the time they were counted, and every count since.',
};

function InventoryScreen() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const badges = useBadges();

  const patch = (next: Partial<InventorySearch>) => {
    void navigate({
      search: (prev) => ({ ...prev, ...next }),
      // Typing in a filter should not fill the history stack, but moving
      // between tabs should — Back is how an operator leaves a sub-view.
      replace: next.tab === undefined || next.tab === search.tab,
    });
  };

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: '⏎', label: 'Open' },
  ]);

  const tabProps = { search, patch };

  return (
    <Page>
      <PageHeader
        eyebrow="Stock"
        title="Inventory"
        subtitle={TAB_SUBTITLE[search.tab]}
        tabs={
          <SubNav>
            {INVENTORY_TABS.map((tab) => (
              <SubNavItem
                key={tab}
                active={search.tab === tab}
                count={tab === 'low' ? badges.lowStock : undefined}
                onClick={() =>
                  // A tab switch resets pagination but keeps the search text and
                  // the department, because those describe *what* the operator is
                  // looking at, not which view of it they want.
                  patch({ tab, page: 1 })
                }
              >
                {TAB_LABELS[tab]}
              </SubNavItem>
            ))}
          </SubNav>
        }
      />

      {search.tab === 'stock' ? <StockTab {...tabProps} /> : null}
      {search.tab === 'low' ? <LowStockTab {...tabProps} /> : null}
      {search.tab === 'expiring' ? <ExpiringTab {...tabProps} /> : null}
      {search.tab === 'lots' ? <LotsTab {...tabProps} /> : null}
      {search.tab === 'tracking' ? <TrackingTab {...tabProps} /> : null}
      {search.tab === 'import' ? <ImportTab /> : null}
      {search.tab === 'counts' ? <CountsTab {...tabProps} /> : null}
    </Page>
  );
}
